import { useAuth } from "@/contexts/AuthContext";
import Modal, { ModalCTAs } from "@/components/molecules/Modal";
import Stepper from "@/components/molecules/Stepper";
import { useStepperStore } from "@/stores/stepperStore";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import styles from "./styles.module.css";
import Tooltip from "@/components/atoms/Tooltip";
import CTA from "@/components/atoms/CTA";
import { useTranslation } from "react-i18next";
import React, { useRef, useMemo, useEffect, useState } from "react";
import { useGetCompanyById } from "@/services/Company";
import BankAccountStep from "@/components/organisms/AccountGate/Steps/BankAccountStep";
import type { Address, BankAccount } from "@/types";
import CompanyAddressStep from "@/components/organisms/AccountGate/Steps/CompanyAddressStep";
import CompanyDetailsStep from "@/components/organisms/AccountGate/Steps/CompanyDetailsStep";
import { useQueryClient } from "@tanstack/react-query";
import useIndexedRef from "@/hooks/useIndexedRef";

type Props = {
  children: React.ReactNode;
  requiredFields?: string[];
};

const isFilled = (v: unknown) => v !== undefined && v !== null && String(v).trim() !== "";

const isBankAccountValid = (bankAccounts?: BankAccount[] | null) => {
  if (!bankAccounts || bankAccounts.length === 0) return false;
  const acct = bankAccounts[0];
  return isFilled(acct.accountNo) && isFilled(acct.accountNo) && isFilled(acct.iban);
};

const isAddressValid = (addresses?: Address[] | null) => {
  if (!addresses || addresses.length === 0) return false;
  const addr = addresses[0];
  return isFilled(addr.address) && isFilled(addr.place) && isFilled(addr.postCode) && isFilled(addr.country);
};

const isCompanyDetailsValid = (companyData: { [key: string]: any } | undefined) => {
  if (!companyData) return false;
  const requiredFields = ["email", "phone", "companyName", "vatNumber"];
  for (const field of requiredFields) {
    const value = companyData[field];
    if (value === undefined || value === null || String(value).trim() === "") {
      return false;
    }
  }
  return true;
};

const isCompanyDataValid = (
  companyData: { bankAccount?: BankAccount[] | null; companyAddress?: Address[] | null } | undefined
) => {
  if (!companyData) return false;
  const bankValid = isBankAccountValid(companyData?.bankAccount);
  const addressValid = isAddressValid(companyData?.companyAddress);
  const companyDetailsValid = isCompanyDetailsValid(companyData);
  return bankValid && addressValid && companyDetailsValid;
};

export default function AccountGate({ children }: Props) {
  const { currentCompany } = useAuth();
  const { t } = useTranslation();
  const formSections = useRef<(HTMLFormElement | null)[]>([]);
  const setCurrentStep = useStepperStore((s) => s.goTo);
  const currentStep = useStepperStore((s) => s.currentStep);
  const next = useStepperStore((s) => s.next);
  const prev = useStepperStore((s) => s.prev);
  const steps = useStepperStore((s) => s.steps);
  const setSteps = useStepperStore((s) => s.setSteps);
  const { companies, changeCompany } = useAuth();

  const setForm0 = useIndexedRef<HTMLFormElement>(formSections, 0);
  const setForm1 = useIndexedRef<HTMLFormElement>(formSections, 1);
  const setForm2 = useIndexedRef<HTMLFormElement>(formSections, 2);

  useEffect(() => {
    setSteps(3);
  }, [setSteps]);

  const queryClient = useQueryClient();

  const {
    data: companyData,
    isLoading,
    isFetched,
    isError,
  } = useGetCompanyById(currentCompany?.companyId as number, {
    enabled: !!currentCompany?.companyId,
  });

  const valid = useMemo(() => {
    return isCompanyDataValid(companyData);
  }, [companyData, isLoading]);

  const [closedByFinish, setClosedByFinish] = useState(false);

  useEffect(() => {
    if (valid) {
      setClosedByFinish(false);
    }
  }, [valid]);

  if (!isFetched) return <>{children}</>;
  if (isError) return <>{children}</>;
  if (valid || isLoading) return <>{children}</>;

  const validateCurrentStep = () => {
    const form = formSections.current[currentStep];
    if (form) {
      form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
    }
  };

  const onFormSectionValidationSuccess = async () => {
    if (currentStep === steps - 1) {
      // last step completed: invalidate company query and close modal
      try {
        await queryClient.invalidateQueries({
          queryKey: ["company", currentCompany?.companyId],
        });
      } finally {
        setClosedByFinish(true);
      }
    } else {
      next();
    }
  };

  const handleClose = async () => {
    const allOtherCompaniesButCurrent = companies.filter((company) => company.companyId !== currentCompany?.companyId);
    if (allOtherCompaniesButCurrent.length > 0) {
      await changeCompany(allOtherCompaniesButCurrent[0].companyId);
    } else {
      setClosedByFinish(true);
    }
  };

  return (
    <>
      {children}
      <Modal
        opened={!valid && !closedByFinish}
        onClose={() => handleClose()}
        title={t("company:missingCompanyInformation")}
        showCloseButton={true}
        size={"xl"}
      >
        <Stepper
          active={currentStep}
          onStepClick={setCurrentStep}
          allowNextStepsSelect={true}
          classNames={{
            stepIcon: styles.stepIcon,
            step: styles.step,
            stepLabel: styles.stepLabel,
            separator: styles.separator,
            steps: styles.stepperHeader,
            content: styles.stepperContent,
          }}
          size="sm"
          completedIcon={<SVGAsset name="step-complete" />}
        >
          <Stepper.Step
            label={t("settings:bankDetails")}
            icon={
              <Tooltip label={t("settings:bankDetails")}>
                <span>1</span>
              </Tooltip>
            }
          >
            {currentCompany?.companyId && (
              <BankAccountStep
                ref={setForm0}
                onValidationSuccess={onFormSectionValidationSuccess}
                companyId={currentCompany?.companyId}
                bankAccount={companyData?.bankAccount?.[0]}
              />
            )}
          </Stepper.Step>
          <Stepper.Step
            label={t("settings:companySettings.companyDetails")}
            icon={
              <Tooltip label={t("settings:companySettings.companyDetails")}>
                <span>2</span>
              </Tooltip>
            }
          >
            {currentCompany?.companyId && (
              <CompanyDetailsStep
                ref={setForm1}
                onValidationSuccess={onFormSectionValidationSuccess}
                companyData={companyData}
                companyId={currentCompany?.companyId}
              />
            )}
          </Stepper.Step>
          <Stepper.Step
            label={t("settings:transport")}
            icon={
              <Tooltip label={t("settings:transport")}>
                <span>3</span>
              </Tooltip>
            }
          >
            {currentCompany?.companyId && (
              <CompanyAddressStep
                ref={setForm2}
                onValidationSuccess={onFormSectionValidationSuccess}
                missingAddress={companyData?.companyAddress?.[0]}
                companyId={currentCompany?.companyId}
              />
            )}
          </Stepper.Step>
        </Stepper>
        <ModalCTAs>
          <CTA id="back-create-sales-order" intent="negate" onClick={prev}>
            {t("actions:cancel")}
          </CTA>
          {currentStep > 0 && (
            <CTA id="back-create-sales-order" intent="secondary" onClick={prev}>
              {t("actions:back")}
            </CTA>
          )}
          <CTA id="next-create-sales-order" intent="primary" onClick={validateCurrentStep} loading={false}>
            {t("actions:next")}
          </CTA>
        </ModalCTAs>
      </Modal>
    </>
  );
}
